
import React, { useState, useRef, useEffect } from "react";
import FilterButton from "@hyro-components/hyro-filter/components/filter-button/FilterButton";
import { BUTTON_DISPLAY_MODES } from "@hyro-components/hyro-filter/HyroFilter.consts";
import { Stack, styled, Box } from "@mui/material";
import FilterItem from "@hyro-components/hyro-filter/components/filter-item/FilterItem";
import useAppStore from "src/stores/appStore";
import { hasAddedFilterSelector, setHasAddedFilterSelector } from "src/stores/selectors/collectionsSelectors";
import Tooltip from "@hyro-components/Tooltip";
import { HyroButton } from "@hyro-components";
import { BUTTON_COLORS, BUTTON_SIZES, BUTTON_VARIANTS } from "@hyro-components/hyro-button/HyroButton.consts";

const EMPTY_ARRAY = [];

const FilterPanel = ({
  disabled,
  activeFilters = EMPTY_ARRAY,
  inactiveFilters = EMPTY_ARRAY,
  isBulkEditActive = false,
  handleChangeFilterField,
  handleRemoveFilter,
  handleClearAll,
  handleAddFilter
}) => {
  const setHasAddedFilter = useAppStore(setHasAddedFilterSelector);
  const hasAddedFilter = useAppStore(hasAddedFilterSelector);
  const [activeFilterKey, setActiveFilterKey] = useState(null);
  const [showShadow, setShowShadow] = useState(false);
  const containerRef = useRef(null);
  const prevActiveFiltersRef = useRef([]);

  // Track changes to activeFilters to detect newly added filters
  // This is to ensure that the new filter is selected and focused
  useEffect(() => {
    if (
      !hasAddedFilter ||
            activeFilters.length !== prevActiveFiltersRef.current.length + 1
    ) return;

    const newFilter = activeFilters[activeFilters.length - 1];
    const needsScrolling = containerRef.current.scrollWidth > containerRef.current.clientWidth;

    if (needsScrolling) {
      const handleScrollEnd = () => {
        setTimeout(() => setActiveFilterKey(newFilter.key));
        containerRef.current.removeEventListener("scrollend", handleScrollEnd);
      };
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
      containerRef.current.addEventListener("scrollend", handleScrollEnd);
    } else {
      setActiveFilterKey(newFilter.key);
    }

    prevActiveFiltersRef.current = [...activeFilters];
    setHasAddedFilter(false);
  }, [activeFilters, setHasAddedFilter, hasAddedFilter]);

  // Track changes to the container to detect if the scroll is at the end of the container
  // This is to show the shadow on the right side of the container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return () => { };

    const handleScrollEnd = () => {
      const { scrollWidth, clientWidth, scrollLeft } = container;
      const isAtEnd = Math.abs(scrollWidth - clientWidth - scrollLeft) < 1;

      setShowShadow(!isAtEnd);
    };

    handleScrollEnd();
    container.addEventListener("scroll", handleScrollEnd, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScrollEnd);
    };
  }, [containerRef]);

  return (
    <Tooltip
      title={isBulkEditActive ? "Filtering while using bulk edit isn’t supported" : ""}
      tooltipStyle={{
        maxWidth: "146px",
        translate: "0 -2px"
      }}
      stackStyle={{
        display: "grid",
        gridTemplateColumns: "repeat(3, auto)",
        alignItems: "center",
        gap: (theme) => theme.spacing(1),
        marginBottom: (theme) => theme.spacing(0.5),
        width: "fit-content"
      }}
    >
      <StyledFilterItemsContainer data-testid="filter-panel" ref={containerRef}>
        {activeFilters.map((filter, index) => (
          <FilterItem
            testId={`filter-item-${index}`}
            id={filter.key}
            key={filter.key}
            label={filter.label}
            value={filter.value}
            type={filter.overrideType || filter.type}
            disabled={disabled}
            options={filter.options}
            isActive={activeFilterKey === filter.key}
            onClick={() => setActiveFilterKey(filter.key)}
            onChange={({ value, operator }) => handleChangeFilterField(value, filter, operator)}
            onClose={() => setActiveFilterKey(null)}
            onRemove={() => {
              setActiveFilterKey(null);
              prevActiveFiltersRef.current = prevActiveFiltersRef.current.filter(f => f.key !== filter.key);
              handleRemoveFilter(filter.key);
            }}
            operator={filter.operator}
          />
        ))}
      </StyledFilterItemsContainer>

      <StyledFilterButtonContainer showShadow={showShadow}>
        <FilterButton
          disabled={disabled}
          displayMode={BUTTON_DISPLAY_MODES.ICON_TEXT}
          filterOptions={inactiveFilters}
          onFilterOptionClick={handleAddFilter}
          testId="filter-button"
        />
      </StyledFilterButtonContainer>

      <HyroButton
        disabled={disabled}
        variant={BUTTON_VARIANTS.TEXT}
        color={BUTTON_COLORS.SECONDARY}
        size={BUTTON_SIZES.SEMI_MEDIUM}
        data-testid="filter-button-clear-button"
        onClick={handleClearAll}
        sx={{
          marginBottom: (theme) => theme.spacing(1.5)
        }}
      >
        Clear all
      </HyroButton>
    </Tooltip>
  );
};

const StyledFilterItemsContainer = styled(Stack)(({ theme }) => ({
  maxWidth: "100%",
  overflowX: "auto",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1),
  paddingBottom: theme.spacing(1.5),
  "& > *": {
    flexShrink: 0
  },
  scrollBehavior: "smooth"
}));

const StyledFilterButtonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showShadow"
})(({ theme, showShadow }) => ({
  marginBottom: theme.spacing(1.5),
  ...(showShadow && {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "-45px",
      width: "45px",
      height: "100%",
      background: "linear-gradient(to right, rgba(250, 250, 250, 0) 20%, #FAFAFA 100%)"
    }
  })
}));

export default FilterPanel;
